/** Cálculo de burden TC / TT — plantilla EMCALI (Anexo 2). */

export type BurdenTipoCalculo = 'tc' | 'tt';

export type BurdenMedidorHomologado = {
  id: string;
  nombre: string;
  cto_corriente_va: number;
  cto_tension_va: number;
  activo?: boolean;
};

export type BurdenEntradaTc = {
  tipo: 'tc';
  i_secundaria_a: number;
  medidor_ids: string[];
  calibre_awg: number;
  r_ac_ohm_por_m: number;
  longitud_conductor_m: number;
  burden_nominal_va: number;
  descripcion?: string;
};

export type BurdenEntradaTt = {
  tipo: 'tt';
  v_secundaria: number;
  medidor_ids: string[];
  calibre_awg: number;
  r_ac_ohm_por_m: number;
  longitud_conductor_m: number;
  burden_nominal_va: number;
  descripcion?: string;
};

export type BurdenEntradaCalculo = BurdenEntradaTc | BurdenEntradaTt;

export type BurdenResultadoCalculo = {
  va_medidor: number;
  va_conductor: number;
  burden_total_va: number;
  burden_minimo_va: number;
  burden_nominal_va: number;
  pct_nominal: number;
  cumple: boolean;
};

export const BURDEN_I_SECUNDARIA_OPCIONES = [1, 5] as const;
export const BURDEN_V_SECUNDARIA_OPCIONES = [57.7, 120, 240] as const;
export const BURDEN_NOMINAL_OPCIONES = [2.5, 5, 10, 15, 25, 30] as const;

export function sumaVaMedidores(
  medidorIds: string[],
  campo: 'cto_corriente_va' | 'cto_tension_va',
  catalogo: BurdenMedidorHomologado[],
): number {
  const map = new Map(catalogo.map((m) => [String(m.id), m]));
  let sum = 0;
  for (const id of medidorIds || []) {
    const key = String(id || '').trim();
    if (!key || key === 'ninguno') continue;
    const m = map.get(key);
    if (!m) continue;
    sum += Number(m[campo]) || 0;
  }
  return sum;
}

export function calcularVaConductorTc(
  iSecundariaA: number,
  longitudM: number,
  rAcOhmPorM: number,
): number {
  const I = Number(iSecundariaA);
  const l = Number(longitudM) || 0;
  const r = Number(rAcOhmPorM) || 0;
  if (!Number.isFinite(I) || I <= 0) return 0;
  return I * I * l * r;
}

export function calcularVaConductorTt(
  vSecundaria: number,
  vaMedidor: number,
  longitudM: number,
  rAcOhmPorM: number,
): number {
  const V = Number(vSecundaria);
  const vaM = Number(vaMedidor) || 0;
  const l = Number(longitudM) || 0;
  const r = Number(rAcOhmPorM) || 0;
  if (!Number.isFinite(V) || V <= 0 || l <= 0 || r <= 0) return 0;
  const rL = r * l;
  const denom = rL + (vaM > 0 ? (V * V) / vaM : 0);
  if (denom <= 0) return 0;
  const iEq = V / denom;
  return iEq * iEq * rL;
}

export function evaluarCumplimientoBurden(burdenTotal: number, burdenNominal: number) {
  const nominal = Number(burdenNominal) || 0;
  const total = Number(burdenTotal) || 0;
  const minimo = nominal > 0 ? nominal * 0.25 : 0;
  const pct = nominal > 0 ? (total / nominal) * 100 : 0;
  const cumple = nominal > 0 && total >= minimo && total <= nominal;
  return { burden_minimo_va: minimo, cumple, pct_nominal: pct };
}

export function calcularBurdenTc(
  entrada: BurdenEntradaTc,
  catalogo: BurdenMedidorHomologado[],
): BurdenResultadoCalculo | null {
  const I = Number(entrada.i_secundaria_a);
  if (!Number.isFinite(I) || I <= 0) return null;
  const vaMedidor = sumaVaMedidores(entrada.medidor_ids, 'cto_corriente_va', catalogo);
  const vaConductor = calcularVaConductorTc(
    I,
    entrada.longitud_conductor_m,
    entrada.r_ac_ohm_por_m,
  );
  const burdenTotal = vaMedidor + vaConductor;
  const ev = evaluarCumplimientoBurden(burdenTotal, entrada.burden_nominal_va);
  return {
    va_medidor: vaMedidor,
    va_conductor: vaConductor,
    burden_total_va: burdenTotal,
    burden_nominal_va: Number(entrada.burden_nominal_va) || 0,
    ...ev,
  };
}

export function calcularBurdenTt(
  entrada: BurdenEntradaTt,
  catalogo: BurdenMedidorHomologado[],
): BurdenResultadoCalculo | null {
  const V = Number(entrada.v_secundaria);
  if (!Number.isFinite(V) || V <= 0) return null;
  const vaMedidor = sumaVaMedidores(entrada.medidor_ids, 'cto_tension_va', catalogo);
  const vaConductor = calcularVaConductorTt(
    V,
    vaMedidor,
    entrada.longitud_conductor_m,
    entrada.r_ac_ohm_por_m,
  );
  const burdenTotal = vaMedidor + vaConductor;
  const ev = evaluarCumplimientoBurden(burdenTotal, entrada.burden_nominal_va);
  return {
    va_medidor: vaMedidor,
    va_conductor: vaConductor,
    burden_total_va: burdenTotal,
    burden_nominal_va: Number(entrada.burden_nominal_va) || 0,
    ...ev,
  };
}

export function calcularBurdenDesdeEntrada(
  entrada: BurdenEntradaCalculo,
  catalogo: BurdenMedidorHomologado[],
): BurdenResultadoCalculo | null {
  if (entrada.tipo === 'tt') return calcularBurdenTt(entrada, catalogo);
  return calcularBurdenTc(entrada, catalogo);
}

export function formatBurdenNum(value: number, decimales = 4): string {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("es-CO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimales,
  });
}

export function parseDistanciaMetros(value: unknown): number | null {
  const n = Number(String(value ?? "").replace(",", ".").trim());
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

export function longitudSugeridaDesdeProyecto(distanciaRedM: unknown): number | null {
  const d = parseDistanciaMetros(distanciaRedM);
  if (d == null || d <= 0) return null;
  return Math.max(1, d);
}
